import { applyPlaceholderPreview } from "@/lib/templatePlaceholders";
import { templateIncludesSalutation, templateIncludesSignature } from "@/lib/templateConditions";
import { resolveBookingUrl, resolveShopUrl } from "@/lib/publicUrls";

const EMAIL_HTML = /<\s*(p|div|table|tr|td|h[1-6]|ul|ol|li|a|strong|em|br|span|img|b|i|u|hr)\b/i;

export function looksLikeEmailHtml(value: string): boolean {
  return EMAIL_HTML.test(value);
}

/** Leer, wenn kein sichtbarer Text, kein Platzhalter und kein Bild oder Block übrig ist. */
export function isBlankMailText(value: string | null | undefined): boolean {
  const source = value ?? "";
  if (/\{\{/.test(source)) return false;
  if (/<(img|hr|table|ul|ol)\b/i.test(source)) return false;
  const text = source
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\u00a0/g, " ")
    .trim();
  return text.length === 0;
}

export function emailButtonHtml(href: string, label: string): string {
  const safeHref = href.replace(/"/g, "%22");
  return (
    '<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:16px 0;border-collapse:collapse;">' +
    '<tr><td align="center" bgcolor="#166534" style="background-color:#166534;border-radius:8px;mso-padding-alt:12px 22px;">' +
    `<a href="${safeHref}" style="display:inline-block;padding:12px 22px;font-family:Arial,Helvetica,sans-serif;font-size:15px;line-height:1.2;font-weight:700;color:#ffffff;text-decoration:none;">${label}</a>` +
    "</td></tr></table>"
  );
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function plainParagraphs(escaped: string): string {
  const stripped = escaped.trim();
  if (!stripped) return "";
  return stripped
    .split(/\n[ \t]*\n/)
    .map((block) => {
      const inner = block
        .split("\n")
        .map((line) => line || "&nbsp;")
        .join("<br>");
      return `<p>${inner}</p>`;
    })
    .join("");
}

/** Enter im Editor erzeugt div-Blöcke. In der Mail sind das Absätze. */
export function promoteSimpleDivs(root: HTMLElement) {
  const divs = Array.from(root.querySelectorAll("div"));
  divs.reverse();
  for (const div of divs) {
    if (
      div.hasAttribute("data-mail-if")
      || div.hasAttribute("data-mail-unless")
      || div.hasAttribute("data-mail-block")
      || div.hasAttribute("data-mail-if-body")
      || div.hasAttribute("data-mail-if-chrome")
    ) {
      continue;
    }
    if (div.closest("table[data-mail-card]")) continue;
    if (div.querySelector("table, div, ul, ol, hr, h1, h2, h3, p")) continue;
    const paragraph = document.createElement("p");
    paragraph.innerHTML = div.innerHTML || "<br>";
    const style = div.getAttribute("style");
    if (style) paragraph.setAttribute("style", style);
    div.replaceWith(paragraph);
  }
}

const PARAGRAPH_STYLE = "<style>p{margin:0 0 12px;}</style>";

function withParagraphStyle(html: string): string {
  if (/<\/head>/i.test(html)) return html.replace(/<\/head>/i, `${PARAGRAPH_STYLE}</head>`);
  return `${PARAGRAPH_STYLE}${html}`;
}

export type FactsPalette = {
  bg: string;
  border: string;
  label: string;
  value: string;
  note: string;
  font: string;
};

const FACTS_PALETTES: Record<string, FactsPalette> = {
  classic: {
    bg: "#f4f4f5",
    border: "#d4d4d8",
    label: "#3f3f46",
    value: "#18181b",
    note: "#52525b",
    font: "Arial, Helvetica, sans-serif",
  },
  modern: {
    bg: "#eff6ff",
    border: "#bfdbfe",
    label: "#1e40af",
    value: "#1e3a8a",
    note: "#1d4ed8",
    font: "Arial, Helvetica, sans-serif",
  },
  nature: {
    bg: "#f3faf5",
    border: "#b7dfc4",
    label: "#1e5c3a",
    value: "#10281c",
    note: "#3d5c4a",
    font: "Arial, Helvetica, sans-serif",
  },
  elegant: {
    bg: "#faf7f2",
    border: "#e4d8c8",
    label: "#6b5344",
    value: "#2a2118",
    note: "#6f6458",
    font: "Georgia, 'Times New Roman', serif",
  },
  minimal: {
    bg: "#f8fafc",
    border: "#e5e7eb",
    label: "#4b5563",
    value: "#111827",
    note: "#6b7280",
    font: "Arial, Helvetica, sans-serif",
  },
  coast: {
    bg: "#f0fdfa",
    border: "#99f6e4",
    label: "#0f766e",
    value: "#134e4a",
    note: "#115e59",
    font: "Arial, Helvetica, sans-serif",
  },
  alpine: {
    bg: "#f8fafc",
    border: "#cbd5e1",
    label: "#334155",
    value: "#0f172a",
    note: "#475569",
    font: "Arial, Helvetica, sans-serif",
  },
  harbor: {
    bg: "#fffbeb",
    border: "#e4b15a",
    label: "#0b1f33",
    value: "#12304d",
    note: "#475569",
    font: "Arial, Helvetica, sans-serif",
  },
  terracotta: {
    bg: "#fff7ed",
    border: "#fdba74",
    label: "#9a3412",
    value: "#431407",
    note: "#7c2d12",
    font: "Arial, Helvetica, sans-serif",
  },
  forest: {
    bg: "#f0fdf4",
    border: "#86efac",
    label: "#166534",
    value: "#052e16",
    note: "#3f6212",
    font: "Arial, Helvetica, sans-serif",
  },
  nordic: {
    bg: "#f4f7f8",
    border: "#d5e2e6",
    label: "#5b7c8a",
    value: "#24343c",
    note: "#4b616c",
    font: "Arial, Helvetica, sans-serif",
  },
  sunset: {
    bg: "#fff7ed",
    border: "#fdba74",
    label: "#c2410c",
    value: "#7c2d12",
    note: "#9a3412",
    font: "Arial, Helvetica, sans-serif",
  },
  letter: {
    bg: "#fffdf8",
    border: "#d6c7b2",
    label: "#6b5344",
    value: "#1c1917",
    note: "#57534e",
    font: "Georgia, 'Times New Roman', serif",
  },
  meadow: {
    bg: "#f7fee7",
    border: "#bef264",
    label: "#3f6212",
    value: "#1a2e05",
    note: "#4d7c0f",
    font: "Arial, Helvetica, sans-serif",
  },
  stone: {
    bg: "#f5f5f4",
    border: "#a8a29e",
    label: "#44403c",
    value: "#1c1917",
    note: "#57534e",
    font: "Arial, Helvetica, sans-serif",
  },
  linen: {
    bg: "#f7f3eb",
    border: "#e7e0d4",
    label: "#8c7355",
    value: "#3f3a34",
    note: "#6b645c",
    font: "Georgia, 'Times New Roman', serif",
  },
  lagoon: {
    bg: "#f0fdfa",
    border: "#5eead4",
    label: "#0f766e",
    value: "#134e4a",
    note: "#115e59",
    font: "Arial, Helvetica, sans-serif",
  },
  frost: {
    bg: "#f0f9ff",
    border: "#bae6fd",
    label: "#0369a1",
    value: "#0c4a6e",
    note: "#075985",
    font: "Arial, Helvetica, sans-serif",
  },
  campfire: {
    bg: "#fff7ed",
    border: "#fdba74",
    label: "#9a3412",
    value: "#431407",
    note: "#7c2d12",
    font: "Arial, Helvetica, sans-serif",
  },
};

export function factsPalette(layoutId: string | null | undefined, layoutHtml?: string | null): FactsPalette {
  const known = layoutId ? FACTS_PALETTES[layoutId] : undefined;
  if (known) return known;
  const theme = layoutHtml?.match(/data-mail-theme="([a-z0-9_-]+)"/i)?.[1];
  if (theme && FACTS_PALETTES[theme]) return FACTS_PALETTES[theme];
  return FACTS_PALETTES.classic!;
}

function upsertStyle(style: string, updates: Record<string, string>): string {
  const normalized: Record<string, string> = {};
  for (const [key, value] of Object.entries(updates)) normalized[key.toLowerCase()] = value;
  const items: { key: string; value: string }[] = [];
  for (const part of style.split(";")) {
    const splitAt = part.indexOf(":");
    if (splitAt < 0) continue;
    const key = part.slice(0, splitAt).trim();
    const value = part.slice(splitAt + 1).trim();
    if (!key || normalized[key.toLowerCase()]) continue;
    items.push({ key, value });
  }
  for (const [key, value] of Object.entries(normalized)) items.push({ key, value });
  return items.map((item) => `${item.key}: ${item.value}`).join("; ");
}

function setStyle(el: HTMLElement, updates: Record<string, string>) {
  const next = upsertStyle(el.getAttribute("style") || "", updates);
  if ((el.getAttribute("style") || "") !== next) el.setAttribute("style", next);
}

/** Info-Karte an das gewählte Mail-Design anpassen. Helle Fläche, dunkle Schrift. */
export function paintFactsCards(
  root: ParentNode,
  layoutId: string | null | undefined,
  layoutHtml?: string | null,
) {
  const palette = factsPalette(layoutId, layoutHtml);
  root.querySelectorAll<HTMLTableElement>('table[data-mail-card="facts"]').forEach((table) => {
    table.setAttribute("bgcolor", palette.bg);
    table.setAttribute("cellpadding", "0");
    table.setAttribute("cellspacing", "0");
    setStyle(table, {
      background: palette.bg,
      "background-color": palette.bg,
      border: `1px solid ${palette.border}`,
      "border-radius": "16px",
      "border-collapse": "separate",
    });
    Array.from(table.querySelectorAll("td")).forEach((td, index) => {
      const updates: Record<string, string> = {
        color: palette.value,
        "font-family": palette.font,
        padding: "16px 14px",
      };
      if (index > 0 || /border-left\s*:/i.test(td.getAttribute("style") || "")) {
        updates["border-left"] = `1px solid ${palette.border}`;
      }
      setStyle(td, updates);
      const blocks = Array.from(td.children).filter((el): el is HTMLElement => el instanceof HTMLElement);
      const label = blocks[0];
      const value = blocks[1];
      if (label) {
        setStyle(label, {
          color: palette.label,
          "font-size": "13px",
          "font-weight": "600",
          "line-height": "1.3",
        });
      }
      if (!value) return;
      setStyle(value, {
        color: palette.value,
        "font-size": "18px",
        "font-weight": "700",
        "line-height": "1.35",
        "margin-top": "8px",
      });
      value.querySelectorAll<HTMLElement>("div, p, span").forEach((node) => {
        const note = node.hasAttribute("data-mail-card-note")
          || /font-size:\s*13px/i.test(node.getAttribute("style") || "");
        setStyle(node, { color: note ? palette.note : palette.value });
      });
    });
  });
}

/** Vorlagentext so, wie er im Layout-Platzhalter {{body}} erscheint. */
export function bodyToPreviewHtml(
  body: string,
  sampleOverrides?: Record<string, string>,
  layoutId?: string | null,
  layoutHtml?: string | null,
): string {
  const withValues = applyPlaceholderPreview(body, sampleOverrides);
  const bookingButton = emailButtonHtml(resolveBookingUrl(), "Zur Buchung");
  const shopButton = emailButtonHtml(resolveShopUrl(), "Zum Shop");
  const withButtons = (value: string) =>
    value
      .replace(/\{\{\s*button_booking\s*\}\}/g, bookingButton)
      .replace(/\{\{\s*button_shop\s*\}\}/g, shopButton);
  if (looksLikeEmailHtml(body)) {
    const holder = document.createElement("div");
    holder.innerHTML = withButtons(withValues);
    promoteSimpleDivs(holder);
    paintFactsCards(holder, layoutId, layoutHtml);
    return holder.innerHTML;
  }
  return plainParagraphs(withButtons(escapeHtml(withValues)));
}

const PREVIEW_FRAME = {
  logo: '<p style="margin:0 0 1rem;"><span style="display:inline-block;padding:8px 14px;background:#e5e7eb;border-radius:6px;font-size:13px;">Logo</span></p>',
  salutation: "<p>Sehr geehrter Herr Mustermann,</p>",
  signature: "<p>Mit freundlichen Grüßen<br>Sonnencamping GmbH</p>",
};

/** Logo wie im Versand: echtes Bild oder leer, wenn keins hinterlegt ist. */
export function emailLogoHtml(logoUrl: string | null | undefined): string {
  const src = (logoUrl || "").trim();
  if (!src) return "";
  const absolute =
    /^(https?:|data:)/i.test(src) || typeof window === "undefined" || !src.startsWith("/")
      ? src
      : `${window.location.origin}${src}`;
  const safe = absolute.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
  return (
    `<p style="margin:0 0 1rem;"><img src="${safe}" alt="" ` +
    'style="max-height:72px;max-width:280px;object-fit:contain;" /></p>'
  );
}

/** Grußformel wie in Buchungs- und Rechnungsmails, mit dem hinterlegten Platznamen. */
export function emailSignatureHtml(companyName: string | null | undefined): string {
  const company = (companyName || "").trim();
  if (!company) return PREVIEW_FRAME.signature;
  return `<p>Mit freundlichen Grüßen<br>${escapeHtml(company)}</p>`;
}

function withoutEmptySignatureChrome(html: string, signature: string): string {
  if (signature.trim()) return html;
  return html
    .replace(/<div\b[^>]*border-top:\s*2px solid #4a9b6a;?[^>]*>\s*<\/div>/gi, "")
    .replace(/<p\b[^>]*margin-top:\s*2\.5rem;[^>]*>\s*<\/p>/gi, "")
    .replace(/<(div|p)\b[^>]*data-mail-signature="1"[^>]*>\s*<\/\1>/gi, "")
    .replace(/<tr\b[^>]*data-mail-signature="1"[^>]*>\s*(?:<td\b[^>]*>\s*<\/td>\s*)+<\/tr>/gi, "");
}

export function renderFinishedMail(
  layoutHtml: string,
  body: string,
  extra = "",
  options?: {
    includeSignature?: boolean;
    sampleOverrides?: Record<string, string>;
    layoutId?: string | null;
    /** Hinterlegtes Logo. Leerstring lässt den Platz weg, undefined nutzt den Beispielrahmen. */
    logoHtml?: string;
    /** Grußformel mit hinterlegtem Platznamen. undefined nutzt den Beispielrahmen. */
    signatureHtml?: string;
  },
): string {
  const includeSignature = options?.includeSignature !== false && !templateIncludesSignature(body);
  const signature = includeSignature ? (options?.signatureHtml ?? PREVIEW_FRAME.signature) : "";
  const values: Record<string, string> = {
    logo: options?.logoHtml ?? PREVIEW_FRAME.logo,
    salutation: templateIncludesSalutation(body) ? "" : PREVIEW_FRAME.salutation,
    body: bodyToPreviewHtml(body, options?.sampleOverrides, options?.layoutId, layoutHtml),
    extra,
    signature,
  };
  const filled = layoutHtml.replace(
    /\{\{\s*(logo|salutation|body|extra|signature)\s*\}\}/g,
    (_match, key: string) => values[key] ?? "",
  );
  return withParagraphStyle(withoutEmptySignatureChrome(filled, signature));
}
